import debounce from "lodash.debounce";

import {
  type ClientNotifications,
  type NotificationType,
  type OwnerNotifications,
  type TrainerNotifications,
} from "@fitness-app/data-models/entities/Notification";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type AppThunk } from "../../../index";
import {
  addNotificationToList,
  deleteNotificationFromList,
  setNumberOfUnreadNotifications,
  subscribeToNotificationsFailed,
  subscribeToNotificationsPending,
  subscribeToNotificationsSuccess,
  updateNotificationsOnList,
} from "../reducer";
import { fetchNotifications } from "./fetchNotifications";
import { unsubscribeFromNotifications } from "./unsubscribeFromNotifications";
import { openRealtimeChannel } from "../../../helpers/openRealtimeChannel";
import { claimRealtimeSlot, ownsRealtimeSlot } from "../../../helpers/realtimeSlot";

export const subscribeToNotifications =
  (filter: "all" | "unread", type?: NotificationType[]): AppThunk =>
  async (dispatch, getState, { auth, subscriptions, db }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "notifications");

    try {
      dispatch(subscribeToNotificationsPending());

      await dispatch(fetchNotifications({ filter, type }));

      const { id } = await getLoggedUser(auth);

      // a newer subscribe or an unsubscribe took the slot while we were fetching
      if (!ownsRealtimeSlot(subscriptions, "notifications", claimedSlot)) {
        return;
      }

      const channelName = `notifications-${id}`;
      const channel = await openRealtimeChannel(db, channelName, subscriptions.realtimeNotificationsChannel);

      // detaching the previous channel is another await - the slot may have changed hands
      if (!ownsRealtimeSlot(subscriptions, "notifications", claimedSlot)) {
        await db.removeChannel(channel);
        return;
      }

      subscriptions.notificationsChannelName = channelName;
      subscriptions.realtimeNotificationsChannel = channel;

      // Create debounced dispatch for notification updates (waits 100ms after last update)
      // This ensures the final state is always processed without missing updates
      const debouncedUpdateDispatch = debounce(
        (notification: ClientNotifications | OwnerNotifications | TrainerNotifications) => {
          dispatch(updateNotificationsOnList(notification));
        },
        100,
        { leading: false, trailing: true },
      );

      subscriptions.realtimeNotificationsChannel
        .on<ClientNotifications | OwnerNotifications | TrainerNotifications>(
          "postgres_changes",
          { event: "*", schema: "public", table: "notification", filter: `ownerId=eq.${id}` },
          (payload) => {
            if (payload.eventType === "UPDATE") {
              // Get current notification state from Redux to avoid redundant updates
              const currentState = getState();
              const existingNotification = currentState.notifications.notifications.find(
                (n) => n.id === payload.new.id,
              );

              // Only dispatch update if the notification actually changed in a meaningful way
              // (not just timestamp updates from optimistic state changes)
              if (
                !existingNotification ||
                existingNotification.status !== payload.new.status ||
                existingNotification.type !== payload.new.type
              ) {
                // Debounce dispatch to ensure final state is processed after updates settle
                debouncedUpdateDispatch(payload.new);
              }
            }
            if (payload.eventType === "INSERT") {
              // A notification the active filter hides is not counted either — the badge mirrors
              // the list, so it never grows by something the user cannot open.
              const matchesFilter = !type?.length || type.includes(payload.new.type);

              if (matchesFilter) {
                dispatch(addNotificationToList(payload.new));

                if (payload.new.status === "unread") {
                  dispatch(setNumberOfUnreadNotifications(getState().notifications.totalUnread + 1));
                }
              }
            }
            if (payload.eventType === "DELETE" && payload.old.id) {
              dispatch(deleteNotificationFromList(payload.old.id));
            }
          },
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            dispatch(subscribeToNotificationsSuccess());
          }
          if (status === "TIMED_OUT") {
            dispatch(unsubscribeFromNotifications());
          }
        });
    } catch {
      dispatch(subscribeToNotificationsFailed());
    }
  };
